import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ASSETS } from '../assets/assetMapping';

const imgVector30 = ASSETS.ARROW_ICON; // Dropdown arrow

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownMenuProps {
  options: DropdownOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  placeholder: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  icon?: string; // Optional icon to display in the button
}

function CheckMark({ size = "small" }: { size?: "small" | "big" | "xs" | "32 FIXED" }) {
  if (size === "small") {
    return (
      <div className="relative w-6 h-6" data-name="size=small" data-node-id="5:1550">
        <div className="absolute h-2 left-[6.5px] top-2 w-[11.5px]" data-node-id="5:1551">
          <div className="absolute inset-[-9.38%_-6.52%]">
            <img alt="" className="block max-w-none w-full h-full" src={imgVector30} />
          </div>
        </div>
      </div>
    );
  }
  return null;
}

export default function DropdownMenu({ 
  options, 
  selectedValue, 
  onSelect, 
  placeholder, 
  isOpen, 
  onToggle, 
  onClose,
  icon
}: DropdownMenuProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: -1000, left: -1000, width: 160 });

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        const rect = buttonRef.current!.getBoundingClientRect();
        const viewport = {
          width: window.innerWidth,
          height: window.innerHeight
        };
        
        // Always start with consistent positioning relative to button
        let top = rect.bottom + 4;
        let left = rect.left;
        
        // Estimate dropdown dimensions
        const estimatedDropdownHeight = Math.min(options.length * 40 + 12, 280); // Cap at max-height
        const minDropdownWidth = Math.max(rect.width, 160);
        
        // Only adjust positioning if dropdown would be completely outside viewport
        // Priority: Keep consistent positioning unless absolutely necessary
        
        // Horizontal adjustment - only if dropdown would be completely cut off
        if (left + minDropdownWidth > viewport.width + 20) { // Allow some overflow before adjusting
          // Try to align to right edge of button first
          const rightAlignedLeft = rect.right - minDropdownWidth;
          if (rightAlignedLeft >= 8) {
            left = rightAlignedLeft;
          } else {
            // Last resort: position with margin from right edge
            left = viewport.width - minDropdownWidth - 8;
          }
        }
        
        // Ensure minimum left margin only if really needed
        if (left < 8) {
          left = 8;
        }
        
        // Vertical adjustment - only if dropdown would be significantly cut off
        if (top + estimatedDropdownHeight > viewport.height + 20) { // Allow some overflow
          const abovePosition = rect.top - estimatedDropdownHeight - 4;
          if (abovePosition >= 8) {
            top = abovePosition;
          } else {
            // Last resort: position with margin from bottom
            top = viewport.height - estimatedDropdownHeight - 8;
          }
        }
        
        // Ensure minimum top margin only if really needed
        if (top < 8) {
          top = 8;
        }
        
        setDropdownPosition({
          top: top,
          left: left,
          width: rect.width
        });
      };
      
      updatePosition();
      
      // Update position on scroll and resize
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, options.length]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleOptionClick = (value: string) => {
    onSelect(value);
    onClose();
  };

  const selectedOption = options.find(option => option.value === selectedValue);
  const displayText = selectedValue === 'any' ? placeholder : (selectedOption?.label || placeholder);
  const isSelected = selectedValue !== 'any';

  // Portal dropdown component
  const portalDropdown = isOpen && (
    <div 
      ref={dropdownRef}
      className="fixed bg-white rounded-[10px] shadow-[0px_24px_48px_0px_rgba(0,0,0,0.12)] border border-blue-black-30/8 border-solid z-[9999] min-w-[160px] max-h-[280px] overflow-hidden"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        minWidth: `${Math.max(dropdownPosition.width, 160)}px`
      }}
    >
      <div className="p-1.5 max-h-[268px] overflow-y-auto">
        {options.map((option, index) => (
          <button
            key={option.value}
            onClick={() => handleOptionClick(option.value)}
            className={`w-full flex gap-2.5 items-center justify-start px-1 py-2.5 relative transition-colors hover:bg-gray-50 ${
              index < options.length - 1 ? 'border-b border-blue-black-30/8' : ''
            }`}
          >
            <div className="basis-0 font-inter font-normal grow leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[14px] text-left">
              <p className={`leading-[1.4] ${
                selectedValue === option.value ? 'text-royal' : 'text-blue-black-90'
              }`}>
                {option.label}
              </p>
            </div>
            {selectedValue === option.value && (
              <div className="overflow-clip relative shrink-0 w-6 h-6">
                <CheckMark size="small" />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Dropdown Button */}
      <button
        ref={buttonRef}
        onClick={onToggle}
        className={`box-border flex gap-0.5 h-8 items-center justify-center relative rounded-lg shrink-0 transition-colors bg-white ${
          isOpen 
            ? 'bg-royal-30 border-royal' 
            : isSelected
            ? 'border-royal'
            : 'border-royal-30 hover:bg-gray-50'
        } ${icon ? 'w-8 md:w-auto md:pl-2 md:pr-4 py-1 p-1 md:p-1' : 'px-4 py-1'}`}
      >
        <div aria-hidden="true" className={`absolute border border-solid inset-0 pointer-events-none rounded-lg ${
          isSelected ? 'border-royal' : 'border-royal-30'
        }`}/>
        
        {/* Optional Icon */}
        {icon && (
          <div className="overflow-clip relative shrink-0 w-6 h-6 flex items-center justify-center">
            <img alt="" className="block max-w-none w-5 h-5" src={icon}/>
          </div>
        )}
        
        {/* Text label */}
        <div className={`capitalize flex flex-col font-inter font-semibold justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-center text-nowrap ${
          icon ? 'hidden md:flex' : ''
        } ${isSelected ? 'text-royal' : 'text-blue-black-90'}`}>
          <p className="leading-[normal] whitespace-pre">
            {displayText}
          </p>
        </div>
      </button>

      {/* Portal dropdown rendered at document.body level */}
      {portalDropdown && createPortal(portalDropdown, document.body)}
    </div>
  );
}
